import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";

import {
  ArticleCardByline,
  ArticleCardProfileLink,
} from "~/components/cih/article-coach-links";
import { CihClosingCta } from "~/components/cih/closing-cta";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/cn";
import { getArticlesIndexFn } from "~/lib/functions/cih/articles";
import { getPublishedChallengesFn } from "~/lib/functions/cih/challenges";
import {
  filterByChallengeSlug,
  groupGuidesByChallenge,
  partitionArticles,
  type ChallengeOption,
  type GuideGroup,
} from "~/lib/shared/cih/articles-index";
import { buildArticlesIndexJsonLd } from "~/lib/shared/cih/articles-index-jsonld";
import { createBrandMeta } from "~/lib/shared/meta";

type ArticleRow = Awaited<ReturnType<typeof getArticlesIndexFn>>[number];

/**
 * The active challenge filter lives in the URL so a filtered view is
 * shareable and survives back-navigation. It deliberately does NOT feed
 * `loaderDeps`: filtering is client-side over the already-loaded feed, so a
 * chip click must not re-run the loader.
 */
const searchSchema = z.object({
  challenge: z.string().optional(),
});

export const Route = createFileRoute("/_public/articles/")({
  staticData: { title: "Articles" },
  validateSearch: searchSchema,
  loader: async () => {
    const [articles, challenges] = await Promise.all([
      getArticlesIndexFn(),
      getPublishedChallengesFn(),
    ]);
    return { articles, challenges };
  },
  head: ({ loaderData }) => {
    const base = createBrandMeta({
      title: "Articles | Dream Coach Match",
      description:
        "Practical, expert-written articles from credentialed coaches on life's biggest transitions.",
      ogUrl: "https://dreamcoachmatch.com/articles",
      // Pinned to the unfiltered URL on purpose: `?challenge=` views are
      // convenience state, not pages we want indexed — `/coaching-for/{slug}`
      // is the indexable per-challenge page and filtered views would
      // cannibalize it.
      canonical: "https://dreamcoachmatch.com/articles",
    });
    const jsonLd = buildArticlesIndexJsonLd(
      (loaderData?.articles ?? []).map((r) => ({
        slug: r.article.slug,
        title: r.article.title,
      })),
    );
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(jsonLd) },
      ],
    };
  },
  component: ArticlesIndex,
});

function ArticlesIndex() {
  const { articles, challenges } = Route.useLoaderData();
  const { challenge } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const selected = challenge ?? null;

  // A challenge with no slug has no `/coaching-for` page, so it can neither
  // head a group nor back a chip; its guides fall to Essentials instead.
  const challengeOptions = useMemo<ChallengeOption[]>(
    () =>
      challenges
        .filter((c): c is typeof c & { slug: string } => c.slug != null)
        .map((c) => ({ slug: c.slug, title: c.title })),
    [challenges],
  );

  // Split once, filter on selection. The split is by `coach === null` (house)
  // vs coach-authored — the same partition the DB XOR constraint guarantees, so
  // every article lands in exactly one section.
  const { guides, coachArticles } = useMemo(
    () => partitionArticles(articles),
    [articles],
  );

  const guideGroups = useMemo(
    () =>
      groupGuidesByChallenge(
        filterByChallengeSlug(guides, selected),
        challengeOptions,
      ),
    [guides, selected, challengeOptions],
  );
  const filteredCoachArticles = useMemo(
    () => filterByChallengeSlug(coachArticles, selected),
    [coachArticles, selected],
  );

  const handleSelect = (slug: string | null) => {
    navigate({ search: { challenge: slug ?? undefined } });
  };

  return (
    <>
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-6 pb-10 md:pt-8 md:pb-12 xl:px-0">
          <Breadcrumb
            items={[{ label: "Home", href: "/" }, { label: "Articles" }]}
            isBold={false}
          />
          <div className="mt-6 text-xs font-medium tracking-[0.22em] text-purple-700 uppercase">
            ● Coaching Intelligence
          </div>
          <h1 className="mt-4 font-heading text-4xl font-medium tracking-tight text-gray-900 md:text-5xl lg:text-6xl xl:text-7xl">
            Articles
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-normal text-gray-700">
            Reference guides from Dream Coach Match, plus expert articles from
            credentialed coaches on the platform.
          </p>
        </div>
      </section>

      <ChallengeFilterRow
        options={challengeOptions}
        selected={selected}
        onSelect={handleSelect}
      />

      <GuidesSection groups={guideGroups} filtered={selected !== null} />

      <CoachArticlesSection
        articles={filteredCoachArticles}
        filtered={selected !== null}
      />

      <CihClosingCta
        headline="Not sure where to start?"
        cta={
          <Link to="/intake">
            <Button variant="primary" size="lg">
              Take the intake →
            </Button>
          </Link>
        }
      />
    </>
  );
}

/**
 * Chip row ("All" + every live challenge) filtering both sections at once. On
 * mobile the row scrolls sideways rather than wrapping or hiding options,
 * keeping the sections reachable below it.
 */
function ChallengeFilterRow({
  options,
  selected,
  onSelect,
}: {
  readonly options: readonly ChallengeOption[];
  readonly selected: string | null;
  readonly onSelect: (slug: string | null) => void;
}) {
  return (
    <div className="sticky top-[var(--dcm-sticky-offset)] z-10 border-b border-gray-200 bg-white/95 backdrop-blur transition-[top] duration-300 ease-out motion-reduce:transition-none">
      <div className="mx-auto max-w-7xl px-4 xl:px-0">
        <div
          role="group"
          aria-label="Filter articles by challenge"
          className="flex [scrollbar-width:none] gap-2 overflow-x-auto py-4 [&::-webkit-scrollbar]:hidden"
        >
          <FilterChip
            label="All"
            active={selected === null}
            onClick={() => onSelect(null)}
          />
          {options.map((option) => (
            <FilterChip
              key={option.slug}
              label={option.title}
              active={selected === option.slug}
              onClick={() => onSelect(option.slug)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  readonly label: string;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "shrink-0 rounded-full whitespace-nowrap",
        active
          ? "border-gray-900 bg-gray-900 text-white shadow-none hover:bg-gray-900 hover:text-white"
          : "text-gray-600",
      )}
    >
      {label}
    </Button>
  );
}

/**
 * House "Guides" — grouped by challenge, never date-sorted. A reference shelf,
 * permanently at the top of the page.
 */
function GuidesSection({
  groups,
  filtered,
}: {
  readonly groups: readonly GuideGroup<ArticleRow>[];
  readonly filtered: boolean;
}) {
  return (
    <section className="border-b border-gray-200">
      <div className="mx-auto max-w-7xl px-4 pt-12 pb-4 md:pt-16 xl:px-0">
        <div className="text-xs font-medium tracking-[0.22em] text-gray-400 uppercase">
          Guides
        </div>
        <h2 className="mt-3 font-heading text-3xl font-medium tracking-tight text-gray-900">
          Start with the essentials
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600">
          Definitional and comparison guides from Dream Coach Match, organized
          by the challenge they speak to.
        </p>
      </div>

      <div className="mx-auto max-w-7xl px-4 pb-14 xl:px-0">
        {groups.length === 0 ? (
          <p className="text-sm text-gray-500">
            {filtered
              ? "No guides for this challenge yet."
              : "No guides published yet."}
          </p>
        ) : (
          groups.map((group) => (
            <GuideGroupBlock key={group.key} group={group} />
          ))
        )}
      </div>
    </section>
  );
}

function GuideGroupBlock({
  group,
}: {
  readonly group: GuideGroup<ArticleRow>;
}) {
  return (
    <div className="mt-10 first:mt-6">
      {group.slug ? (
        <Link
          to="/coaching-for/$challengeSlug"
          params={{ challengeSlug: group.slug }}
          className="group inline-flex items-center gap-1.5 text-lg font-medium tracking-tight text-gray-900 hover:text-purple-700"
        >
          {group.title}
          <span
            aria-hidden
            className="text-gray-300 transition-colors group-hover:text-purple-700"
          >
            →
          </span>
        </Link>
      ) : (
        <h3 className="text-lg font-medium tracking-tight text-gray-900">
          {group.title}
        </h3>
      )}

      {/* Each card carries its own border rather than the container painting
          gaps between flush cells: a group with fewer guides than columns would
          otherwise leave the container background showing as a large empty
          block. */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {group.articles.map((item) => (
          <GuideCard key={item.article.id} item={item} />
        ))}
      </div>
    </div>
  );
}

function GuideCard({ item }: { readonly item: ArticleRow }) {
  return (
    <Link
      to="/articles/$slug"
      params={{ slug: item.article.slug }}
      className="flex flex-col rounded-xl border border-gray-200 bg-white p-5 transition-colors hover:bg-gray-50"
    >
      <div className="text-[0.65rem] font-medium tracking-[0.18em] text-gray-400 uppercase">
        {item.challenge.title}
      </div>
      <h4 className="mt-2 font-heading text-lg font-medium tracking-tight text-gray-900">
        {item.article.title}
      </h4>
      <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-gray-600">
        {item.article.description}
      </p>
    </Link>
  );
}

/**
 * Coach-authored articles — the card leads with the human (avatar, name,
 * credentials, profile link). Recency order is preserved from the loader query.
 */
function CoachArticlesSection({
  articles,
  filtered,
}: {
  readonly articles: readonly ArticleRow[];
  readonly filtered: boolean;
}) {
  return (
    <section>
      <div className="mx-auto max-w-7xl px-4 pt-14 pb-4 md:pt-16 xl:px-0">
        <div className="text-xs font-medium tracking-[0.22em] text-purple-700 uppercase">
          From our coaches
        </div>
        <h2 className="mt-3 font-heading text-3xl font-medium tracking-tight text-gray-900">
          Written by credentialed coaches
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600">
          Expertise from coaches on the platform — the newest first.
        </p>
      </div>

      {articles.length === 0 ? (
        <div className="mx-auto max-w-7xl px-4 pb-20 xl:px-0">
          <p className="text-sm text-gray-500">
            {filtered
              ? "No coach articles for this challenge yet."
              : "No coach articles published yet."}
          </p>
        </div>
      ) : (
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 pb-20 md:grid-cols-2 lg:grid-cols-3 xl:px-0">
          {articles.map((item) => (
            <CoachArticleCard key={item.article.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}

function CoachArticleCard({ item }: { readonly item: ArticleRow }) {
  // This section only ever holds coach-authored rows (coach !== null), but the
  // byline falls back to the editorial name so a data anomaly degrades rather
  // than rendering a card with no author at all.
  const coach = item.coach;
  const credentials = item.coachCcos.map((c) => c.name).join(" · ");

  return (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-5 transition-shadow hover:shadow-md">
      {coach ? (
        <ArticleCardByline coach={coach} credentials={credentials} />
      ) : (
        <div className="text-sm font-medium text-gray-900">
          {item.article.authorName}
        </div>
      )}

      <Link
        to="/articles/$slug"
        params={{ slug: item.article.slug }}
        className="mt-4 block"
      >
        <div className="text-[0.65rem] font-medium tracking-[0.18em] text-gray-400 uppercase">
          {item.challenge.title}
        </div>
        <h3 className="mt-1.5 font-heading text-lg font-medium tracking-tight text-gray-900">
          {item.article.title}
        </h3>
        <p className="mt-1.5 line-clamp-3 text-sm leading-relaxed text-gray-600">
          {item.article.description}
        </p>
      </Link>

      {coach ? <ArticleCardProfileLink coach={coach} /> : null}
    </div>
  );
}
